import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useCurrency, CurrencyToggle } from "@/lib/currency";
import { toast } from "sonner";
import {
  Lock,
  Loader2,
  ShieldCheck,
  Sparkles,
  CheckCircle2,
  Crown,
  AlertTriangle,
  CreditCard,
  Building2,
  Copy,
  Check,
  Send,
} from "lucide-react";

/**
 * Non-dismissible paywall. Renders when the freemium timer has expired
 * and the current project is not unlocked.
 */
export default function PaywallModal({ timer, projectId: explicitProjectId }) {
  const params = useParams();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const { currency, format } = useCurrency();
  const priceLabel = format(69);

  // payment method tabs: "card" | "bank"
  const [method, setMethod] = useState("card");

  // resolve which project to unlock — prefer explicit prop, else url param,
  // else fall back to the user's most recent project
  const [resolvedProjectId, setResolvedProjectId] = useState(
    explicitProjectId || params.projectId || null
  );

  useEffect(() => {
    if (resolvedProjectId) return;
    (async () => {
      try {
        const { data } = await api.get("/projects");
        const top = (data?.projects || [])[0];
        if (top) setResolvedProjectId(top.project_id);
      } catch {
        /* ignore */
      }
    })();
  }, [resolvedProjectId]);

  const buy = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/billing/checkout", {
        package_id: "premium_lifetime_project",
        origin_url: origin,
        project_id: resolvedProjectId || "",
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("Sin URL de pago");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar el pago");
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="paywall-modal"
      className="fixed inset-0 z-[80] flex items-center justify-center px-4 py-6"
    >
      <div className="absolute inset-0 bg-ink/65 backdrop-blur-md" />
      <div className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-3xl bg-paper border border-rule shadow-[0_30px_80px_-20px_rgba(39,59,44,0.55)]">
        {/* Header */}
        <div className="bg-[#1d2c22] text-paper px-7 sm:px-10 py-7 relative overflow-hidden">
          <div aria-hidden="true" className="absolute -top-20 -right-20 h-56 w-56 rounded-full bg-[radial-gradient(closest-side,rgba(193,148,86,0.28),transparent_70%)] blur-2xl" />
          <div className="relative flex items-start gap-4">
            <span className="grid place-items-center h-12 w-12 shrink-0 rounded-full bg-[#E0C58A] text-[#1d2c22]">
              <Lock size={18} />
            </span>
            <div>
              <p className="text-[10px] tracking-[0.28em] uppercase text-[#E0C58A] font-medium">
                Tu prueba gratuita terminó
              </p>
              <h2 className="mt-2 font-serif text-3xl sm:text-[2.1rem] text-paper leading-tight text-balance">
                Tu tesis te está esperando. <span className="italic text-[#E0C58A]">Desbloquéala.</span>
              </h2>
              <p className="mt-2 text-[13.5px] text-paper/75 max-w-lg">
                Tu progreso quedó guardado. El editor está congelado mientras eliges qué hacer.
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-7 sm:px-10 py-7">
          {/* Method tabs */}
          <div
            role="tablist"
            aria-label="Método de pago"
            data-testid="paywall-method-tabs"
            className="grid grid-cols-2 gap-2 p-1 rounded-full bg-paper-2 border border-rule mb-5"
          >
            <button
              type="button"
              role="tab"
              data-testid="paywall-tab-card"
              aria-selected={method === "card"}
              onClick={() => setMethod("card")}
              className={`inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-[13px] font-medium transition-all ${
                method === "card"
                  ? "bg-forest text-paper shadow-sm"
                  : "text-ink-soft hover:text-ink"
              }`}
            >
              <CreditCard size={14} /> Tarjeta (Stripe)
            </button>
            <button
              type="button"
              role="tab"
              data-testid="paywall-tab-bank"
              aria-selected={method === "bank"}
              onClick={() => setMethod("bank")}
              className={`inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-[13px] font-medium transition-all ${
                method === "bank"
                  ? "bg-forest text-paper shadow-sm"
                  : "text-ink-soft hover:text-ink"
              }`}
            >
              <Building2 size={14} /> Transferencia BBVA
            </button>
          </div>

          <div className="rounded-2xl border border-forest/25 bg-white p-5 sm:p-6 relative">
            <span className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-forest text-paper px-3 py-1 text-[10.5px] tracking-[0.18em] uppercase font-medium">
              <Crown size={11} /> Acceso de por vida
            </span>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-baseline gap-2">
                <span
                  data-testid="paywall-price"
                  data-currency={currency}
                  className="font-serif text-5xl text-ink"
                >
                  {priceLabel.replace(/\s(USD|MXN)$/, "")}
                </span>
                <span className="text-[13px] text-ink-soft">{currency} · pago único</span>
              </div>
              <CurrencyToggle testid="paywall-currency-toggle" />
            </div>
            <p className="mt-1 text-[12.5px] text-forest font-medium">
              Una sola vez. Tú decides cuándo terminar tu tesis.
            </p>
            <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="paywall-features">
              <Feature text="Generación de texto ilimitada" />
              <Feature text="Plantilla institucional de tu universidad" />
              <Feature text="Métrica antiplagio + reescritura humana" />
              <Feature text="Sello criptográfico SHA-256 ilimitado" />
              <Feature text="Simulador de jurado completo" />
              <Feature text="Exportación DOCX con formato APA/Vancouver" />
            </ul>
            <p className="mt-4 text-[11.5px] text-ink-soft leading-relaxed">
              Aplica para <span className="text-ink font-medium">1 proyecto de tesis</span> hasta que lo exportes terminado. No es suscripción: no se renueva ni se cobra otra vez.
            </p>
          </div>

          {method === "card" ? (
            <>
              <button
                data-testid="paywall-buy-btn"
                onClick={buy}
                disabled={submitting}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-[14px] font-medium text-[#1d2c22] bg-[#E0C58A] hover:bg-[#EBD8A8] disabled:opacity-70 transition-all shadow-[0_18px_36px_-12px_rgba(224,197,138,0.55)]"
              >
                {submitting ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                {submitting ? "Redirigiendo a Stripe…" : `Desbloquear mi tesis por ${priceLabel}`}
              </button>

              <p className="mt-3 text-center text-[11px] text-ink-soft inline-flex items-center justify-center gap-1.5 w-full">
                <ShieldCheck size={11} className="text-forest" />
                Pago seguro con Stripe · sin renovación automática
              </p>
              {currency === "MXN" && (
                <p className="mt-1 text-center text-[10.5px] text-ink-soft/80">
                  Precio en MXN es referencial. El cargo en Stripe se procesa en USD al tipo de cambio de tu banco.
                </p>
              )}
            </>
          ) : (
            <BankTransferPanel projectId={resolvedProjectId} />
          )}

          {!resolvedProjectId && (
            <p className="mt-3 inline-flex items-center gap-1.5 text-[11.5px] text-copper">
              <AlertTriangle size={11} /> Crea o abre un proyecto antes de pagar para vincular el desbloqueo.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Feature({ text }) {
  return (
    <li className="flex items-start gap-2 text-[12.5px] text-ink">
      <span className="mt-0.5 grid place-items-center h-4 w-4 shrink-0 rounded-full bg-forest text-paper">
        <CheckCircle2 size={10} />
      </span>
      {text}
    </li>
  );
}

function CopyableRow({ label, value, testid }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} copiado`);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("No se pudo copiar");
    }
  };
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-rule/60 last:border-0">
      <div className="min-w-0">
        <p className="text-[10.5px] tracking-[0.18em] uppercase text-ink-soft/80 font-medium">{label}</p>
        <p data-testid={testid} className="mt-0.5 text-[14px] text-ink font-medium break-all">{value}</p>
      </div>
      <button
        type="button"
        data-testid={`${testid}-copy`}
        onClick={onCopy}
        className="shrink-0 inline-flex items-center gap-1 text-[11px] text-forest hover:text-copper font-medium px-2 py-1 rounded-full border border-rule hover:border-forest/30 transition-colors"
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
        {copied ? "Copiado" : "Copiar"}
      </button>
    </div>
  );
}

function BankTransferPanel({ projectId }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/billing/bank-transfer/info");
        setInfo(data);
      } catch (e) {
        toast.error("No se pudo cargar la información bancaria");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const notify = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!projectId) {
      toast.error("Necesitas un proyecto activo para vincular tu transferencia");
      return;
    }
    if (reference.trim().length < 4) {
      toast.error("Captura el número de rastreo/folio de tu banco");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/billing/bank-transfer/notify", {
        project_id: projectId,
        reference_code: reference.trim(),
        note: note.trim() || undefined,
      });
      setSubmitted(true);
      toast.success(data?.message || "Notificación enviada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No fue posible enviar la notificación");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="mt-6 flex items-center justify-center py-10 text-ink-soft">
        <Loader2 size={18} className="animate-spin mr-2" /> Cargando datos bancarios…
      </div>
    );
  }

  if (!info) return null;

  if (submitted) {
    return (
      <div
        data-testid="bank-transfer-success"
        className="mt-6 rounded-2xl border border-forest/30 bg-forest/5 p-6 text-center"
      >
        <span className="inline-grid place-items-center h-12 w-12 rounded-full bg-forest text-paper mx-auto">
          <CheckCircle2 size={20} />
        </span>
        <h3 className="mt-3 font-serif text-2xl text-ink">¡Notificación enviada!</h3>
        <p className="mt-2 text-[13px] text-ink-soft leading-relaxed">
          Validaremos tu transferencia en BBVA y desbloquearemos tu proyecto en
          un plazo máximo de <span className="text-forest font-medium">24 horas hábiles</span>.
          Te avisaremos por correo.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6" data-testid="bank-transfer-panel">
      {/* Bank details card */}
      <div className="rounded-2xl border border-rule bg-paper-2/50 p-5 sm:p-6">
        <div className="flex items-center gap-2">
          <Building2 size={16} className="text-forest" />
          <p className="text-[11px] tracking-[0.18em] uppercase text-forest font-medium">
            Datos para tu transferencia · BBVA México
          </p>
        </div>
        <div className="mt-3 divide-y divide-rule/60">
          <CopyableRow label="Beneficiario" value={info.beneficiario} testid="bank-info-beneficiario" />
          <CopyableRow label="Banco" value={info.banco} testid="bank-info-banco" />
          <CopyableRow
            label="Número de cuenta / CLABE"
            value={info.numero_cuenta}
            testid="bank-info-cuenta"
          />
          <CopyableRow
            label="Monto exacto"
            value={`$${info.monto_mxn.toLocaleString("es-MX")} MXN`}
            testid="bank-info-monto"
          />
        </div>
        <p className="mt-4 text-[12px] text-ink-soft leading-relaxed">
          {info.instrucciones}
        </p>
      </div>

      {/* Notify form */}
      <form onSubmit={notify} className="mt-5 flex flex-col gap-3" data-testid="bank-transfer-form">
        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] tracking-[0.18em] uppercase text-ink-soft font-medium">
            Número de rastreo / folio
          </span>
          <input
            data-testid="bank-transfer-reference"
            type="text"
            required
            minLength={4}
            maxLength={60}
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Ej. 2026062812345678 o folio BBVA"
            className="px-4 py-3 rounded-lg border border-rule bg-white text-[14px] outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 transition"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] tracking-[0.18em] uppercase text-ink-soft font-medium">
            Nota (opcional)
          </span>
          <textarea
            data-testid="bank-transfer-note"
            rows={2}
            maxLength={500}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Si lo prefieres, agrega tu nombre completo o algún dato extra"
            className="px-4 py-3 rounded-lg border border-rule bg-white text-[13px] outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 transition resize-none"
          />
        </label>
        <button
          type="submit"
          data-testid="bank-transfer-submit"
          disabled={submitting || !projectId}
          className="mt-1 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-[14px] font-medium text-paper bg-forest hover:bg-forest-dark disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-sm"
        >
          {submitting ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
          {submitting ? "Enviando notificación…" : "Notifiqué mi transferencia"}
        </button>
        <p className="text-center text-[11px] text-ink-soft inline-flex items-center justify-center gap-1.5 w-full">
          <ShieldCheck size={11} className="text-forest" />
          Validación manual · Hasta 24 hrs hábiles · Sin renovación automática
        </p>
      </form>
    </div>
  );
}
